import { ChangeEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from '@mui/material/Alert';
import {
  Button,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";

import { useAcaoSave } from "./hooks/useAcaoSave";
import { useComplementoAcao } from "./hooks/useComplementoAcao";

/* components */
import SearchSelect from "../../atoms/SearchSelect/SearchSelect";
import SelectField from "../../atoms/SelectField/SelectField";
import MultiSelectCheck from "../../atoms/MultiSelectField/MultSelectCheck";
import ImageUpload from "../../atoms/ImageUpload/ImageUpload";

/* styles */
import styles from './AcoesQualificacao.module.css';
import styles_general from '../../../styles/General.module.css';
import styles_grid from '../../../styles/Grid.module.css';
import styles_button from '../../../styles/Button.module.css';
import styles_filter from '../../../styles/Filter.module.css';
import styles_input from '../../../styles/Input.module.css';

const PERCURSOS_FORMATIVOS = [
  { nome: "Gestão do SUAS" },
  { nome: "Provimento de Serviços e Benefícios" },
  { nome: "Controle Social" },
];

const CadastrarAcao = () => {

  const API_URL_PATH = process.env.REACT_APP_API_URL?.replace("/api", "");

  const [searchParams] = useSearchParams();
  const acaoId = searchParams.get("id");

  const navigate = useNavigate();

  const {
    loadingComplementos,
    tipologias,
    modalidadesAcao,
    temas,
  } = useComplementoAcao();
  
  const {
    msgError,
    msgSuccess,
    loading,
    loadingSave,
    acao,
    isEdit,
    handleInputChange,
    handleChange,
    handleTemasChange,
    saveAcao,
    arquivo,
    setArquivo,
    preview,
    updateArquivo,
    setUpdateArquivo,
    cancelUpdateArquivo,
  } = useAcaoSave(acaoId);

  const [openPdfPreview, setOpenPdfPreview] = useState<boolean>(false);
  const [pdfSrc, setPdfSrc] = useState<string>("");

  const isPdfFile = (file: File | null) => file?.type === "application/pdf";
  const isPdfUrl = (url?: string) =>
    !!url && url.toLowerCase().endsWith(".pdf");

  if (loading || loadingComplementos) {
    return (
      <CircularProgress/>
    );
  }

  return (
    <Card className={styles.container}>
      <CardContent>
        <div className={styles.title}>
          Cadastrar nova ação
        </div>
        {msgSuccess && (
          <div className={styles_general.message}>
            <Alert severity="success">
              {msgSuccess}
            </Alert>
          </div>
        )}
        {msgError && (
          <div className={styles_general.message}>
            <Alert severity="error">
              {msgError}
            </Alert>
          </div>
        )}

        <div className={`${styles_filter.container} ${styles_general.mt6}`}>
          <div className={styles_grid.grid12}>

            <div className={styles_grid.inputGroupFull}>
              <label className={styles_input.titleInput}>
                Nome da Ação *
              </label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                autoFocus={false}
                fullWidth
                data-testid="nome"
                value={acao.nome}
                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                  handleInputChange(e.target.value, 'nome')
                }
                required
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  className: styles_input.input,
                  disableUnderline: true,
                }}
              />
            </div>

            <div className={styles_grid.inputGroupFull}>
              <label className={styles_input.titleInput}>
                Tipologia *
              </label>
              <SearchSelect
                label=""
                key="tipologia_id"
                options={tipologias}
                value={acao.tipologia_id}
                onChange={(id) => {handleChange("tipologia_id", id);}}
                paperClassname={styles_input.paper}
                popperClassname={styles_input.popper}
                labelClassname={styles_input.labelSelect}
                inputClassname={styles_input.inputSelect}
              />
            </div>

            <div className={styles_grid.inputGroupFull}>
              <label className={styles_input.titleInput}>
                Percurso Formativo *
              </label>
              <SelectField
                label=""
                options={PERCURSOS_FORMATIVOS}
                value={acao.percurso_formativo}
                onChange={(value) => { handleChange("percurso_formativo", value); }}
                inputClassName={styles_input.inputSelect}
                disableClearable
                disableTyping
              />
            </div>

            <div className={styles_grid.inputGroup4}>
              <label className={styles_input.titleInput}>
                Modalidade Principal *
              </label>
              <SearchSelect
                label=""
                key="modalidade_id"
                options={modalidadesAcao}
                value={acao.modalidade_id}
                onChange={(id) => {handleChange("modalidade_id", id);}}
                paperClassname={styles_input.paper}
                popperClassname={styles_input.popper}
                labelClassname={styles_input.labelSelect}
                inputClassname={styles_input.inputSelect}
              />
            </div>

            <div className={styles_grid.inputGroup8}>
              <div className={`${styles_grid.flex1} ${styles_grid.wFull}`}>
                  <label className={styles_input.titleInput}>
                    Período de oferta da ação *
                  </label>
                </div>
              <div className={styles_grid.grid12}>
                <div className={styles_grid.inputGroup6}>
                  <TextField
                    type="date"
                    size="small"
                    placeholder="dd/mm/yyyy"
                    variant="standard"
                    autoFocus={false}
                    fullWidth
                    data-testid="data_inicio"
                    value={acao.data_inicio || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(e.target.value, "data_inicio")
                    }
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      className: styles_input.input,
                      disableUnderline: true,
                    }}
                  />
                </div>
                <div className={styles_grid.inputGroup6}>
                  <TextField
                    type="date"
                    size="small"
                    placeholder="dd/mm/yyyy"
                    variant="standard"
                    autoFocus={false}
                    fullWidth
                    data-testid="data_fim"
                    value={acao.data_fim || ""}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(e.target.value, "data_fim")
                    }
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      className: styles_input.input,
                      disableUnderline: true,
                    }}
                  />
                </div>
              </div>
            </div>

            <div className={styles_grid.inputGroupFull}>
              <MultiSelectCheck
                label="Temas(s) Abordado(s) *"
                placeholder="Selecione no mínimo um tema"
                fullWidth
                value={acao.temas.map((d) => d)}
                onChange={handleTemasChange}
                options={temas}
                required
              />
            </div>

            <div className={styles_grid.inputGroupFull}>
              <label className={styles_input.titleInput}>
                Observações
              </label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                autoFocus={false}
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                data-testid="observacoes"
                value={acao.observacoes}
                onChange={(e: ChangeEvent<HTMLInputElement>) => 
                  handleInputChange(e.target.value, 'observacoes')
                }
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  className: styles_input.input,
                  disableUnderline: true,
                }}
              />
            </div>

            {!isEdit || updateArquivo ? (
              <div className={styles_grid.inputGroup6}>
                <label className={styles_input.titleInput}>
                  Upload de card informativo da ação
                </label>
                <ImageUpload
                  label=""
                  value={arquivo}
                  onChange={setArquivo}
                  helperText="PNG, JPG, JPEG e PDF até 2MB"
                  required
                  customClassName={styles_input.input}
                />
                {isEdit && updateArquivo && acao.arquivo && (
                  <div className={styles.optionsPreview}>
                  <Button
                    size="small"
                    variant="outlined"
                    className={styles_button.buttonOutlineDanger}
                    onClick={cancelUpdateArquivo}
                  >
                    Cancelar troca da imagem
                  </Button>
                </div>
                )}
              </div>
            ) : ( null )}

            {isEdit && !updateArquivo && acao.arquivo && (
              <div className={styles_grid.inputGroupFull}>
                <label className={styles_input.titleInput}>
                  Card informativo da ação
                </label>
                <div className={styles.filePreview}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {isPdfUrl(acao.arquivo) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        className={styles_button.buttonPrimary}
                        onClick={() => {
                          setPdfSrc(`${API_URL_PATH}${acao.arquivo}`);
                          setOpenPdfPreview(true);
                        }}
                      >
                        Visualizar PDF
                      </Button>
                    ) : (
                      <img
                        src={`${API_URL_PATH}${acao.arquivo}`}
                        style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    )}
                  </div>
                </div>
                <div className={`${styles_general.mt4} ${styles.optionsPreview}`}>
                  <Button
                    size="small"
                    variant="outlined"
                    className={styles_button.buttonPrimary}
                    onClick={() => setUpdateArquivo(true)}
                  >
                    Trocar Arquivo
                  </Button>
                </div>
              </div>
            )}

            {arquivo && preview && (
              <div className={styles_grid.inputGroup6}>
                <div className={styles.filePreview}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {isPdfFile(arquivo) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        className={styles_button.buttonPrimary}
                        onClick={() => {
                          setPdfSrc(preview);
                          setOpenPdfPreview(true);
                        }}
                      >
                        Visualizar PDF
                      </Button>
                    ) : (
                      <img
                        src={preview}
                        alt={arquivo.name}
                        style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    )}
                  </div>
                </div>
                <div className={`${styles_general.mt4} ${styles.optionsPreview}`}>
                  <Button
                    size="small"
                    variant="outlined"
                    className={styles_button.buttonOutlineDanger}
                    onClick={() => setArquivo(null)}
                  >
                    Remover
                  </Button>
                </div>
              </div>
            )}

          </div>
        </div>

        <div className={`${styles_filter.lineButton} ${styles_general.mt6}`}>
          {/* Limpar */}
          <Button 
            variant="contained"
            onClick={() => navigate(`/acoes-qualificacao`)} 
            className={styles_filter.buttonClean}
            color="primary"
          >
            Voltar
          </Button>
          
          {/* Pesquisar */}
          <Button
            variant="contained"
            onClick={saveAcao}
            className={`${styles_filter.buttonSearch} ${(loadingSave) ? styles_button.buttonLoad : ''} `}
            color="primary"
          >
            {loadingSave ? (
              <>
              <div className={styles_button.spinner}></div>
              <span>Salvando...</span>
              </>
            ) : (
            <span>{isEdit ? "Salvar" : "Próximo"}</span>
            )}
          </Button>                

        </div>

        <Dialog
          open={openPdfPreview}
          onClose={() => setOpenPdfPreview(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Visualização do PDF
            <IconButton onClick={() => setOpenPdfPreview(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ height: '70vh', padding: 0 }}>
            {pdfSrc && (
              <iframe
                src={pdfSrc}
                title="PDF Preview"
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </DialogContent>
        </Dialog>

      </CardContent>
    </Card>
  );

};

export default CadastrarAcao;

